<template>
    <div
        class="dd-calendar"
        v-bind:style="{ backgroundImage: 'url(' + image + ')' }"
        v-loading="loading"
        v-if="success"
    >
        <h1 class="text-center mb-0 mt-0" v-bind:style="{ color: title_color }">
            {{ calendar.default_title }} {{ calendar.formatted_month }}
        </h1>
        <table>
            <thead>
                <tr>
                    <td v-for="(day, i) in weekDayStrings" :key="i">
                        {{ day }}
                    </td>
                </tr>
            </thead>
            <tbody class="no-theme">
                <tr v-for="(week, i) in dates" :key="i">
                    <td
                        :class="{
                            'hide-date':
                                date_info.date.month() + 1 !== calendar.month
                        }"
                        :data-date="date_info.date.format('YYYYMMDD')"
                        :key="date_info.date.format('YYYYMMDD')"
                        :style="{
                            cursor: date_info.editable ? 'pointer' : 'default'
                        }"
                        v-for="date_info in week"
                    >
                        <span class="date">{{ date_info.date.date() }}</span>
                        <div class="cell">
                            <template
                                v-if="
                                    date_info.date.month() + 1 ===
                                    calendar.month
                                "
                            >
                                <div
                                    :class="{
                                        'first-event': i === 0,
                                        'overlaps-date':
                                            date_info.overlaps_date_number,
                                        'all-day': event.is_all_day
                                    }"
                                    :style="getEventStyle(event)"
                                    class="description"
                                    v-for="(event, i) in date_info.events"
                                    :key="i"
                                >
                                    <div class="time float-left text-right">
                                        <span v-if="!event.is_all_day"
                                            >{{
                                                formatEventTime(event)
                                            }}&nbsp;&nbsp;</span
                                        >
                                    </div>
                                    <div
                                        class="description-lines float-right event-title"
                                        v-html="
                                            getEventDisplayText(
                                                event,
                                                i === 0,
                                                date_info.overlaps_date_number
                                            )
                                        "
                                    ></div>
                                    <div class="clearfix"></div>
                                </div>
                            </template>
                        </div>
                    </td>
                </tr>
            </tbody>
        </table>
    </div>
</template>

<script>
import * as MonthlyWeek from "@/constants/monthly-weeks";
import * as RepeatInterval from "@/constants/repeat-intervals";
import event_availability_mixin from "@/mixins/events/availability";
import CalendarUtils, { CalendarCalculator } from "@/utils/calendar";
import { mapState } from "vuex";
export default {
    name: "DigitalDisplayUrl",
    mixins: [event_availability_mixin],
    data() {
        return {
            calendar: null,
            loading: false,
            success: false,
            events: [],
            title_color: null,
            image: "/images/ddbackground.jpeg"
        };
    },
    computed: {
        ...mapState({
            weekDayStrings: (state) => state.enums.weekday_strings
        }),
        code() {
            return this.$route.params.code;
        },
        domain() {
            return window.location.host.split(".")[1]
                ? window.location.host.split(".")[0]
                : false;
        },
        calendarCalculator() {
            return new CalendarCalculator(
                _.pick(this.calendar, ["month", "year", "font", "font_size"])
            );
        },
        dates() {
            let start_date = moment([
                this.calendar.year,
                this.calendar.month - 1
            ]).startOf("month");
            let end_date = start_date.clone().endOf("month");
            let dates = [];
            let week_dates = [];
            this.calendarCalculator.dates.forEach((date) => {
                let events = this.getEventsForDate(date);
                let date_info = {
                    date: date.clone(),
                    events,
                    editable: date.month() + 1 === this.calendar.month,
                    overlaps_date_number:
                        this.calendarCalculator.firstLineOverlapsDateNumber(
                            events
                        )
                };
                week_dates.push(date_info);
                date = date.clone().add(1, "day");
                if (date.day() === 0 || date.isSameOrAfter(end_date)) {
                    dates.push(week_dates);
                    week_dates = [];
                }
            });
            return dates;
        }
    },
    mounted() {
        this.getCalendar();
    },
    methods: {
        getCalendar() {
            let vm = this;
            if (vm.domain != "corwindev") {
                axios
                    .get(
                        `/api/dd-calendar?domain=${vm.domain}&code=${vm.code}&include=events.deleted_events,location_keys,formatted_logo,logo,pricing,community.locations,location,digital_display.preview`
                    )
                    .then((response) => {
                        vm.calendar = response.data;
                        vm.events = response.data.events.data;
                        vm.success = true;
                        if (response.data.digital_display) {
                            vm.title_color =
                                response.data.digital_display.title_color;
                            if (response.data.digital_display.preview) {
                                vm.image =
                                    response.data.digital_display.preview.url;
                            }
                        }
                    })
                    .catch((errors) => {
                        vm.$message({
                            message: "Oops, " + errors.response.data.error,
                            type: "error",
                            duration: 30000000
                        });
                    });
            } else {
                vm.success = false;
            }
        },
        getEventsForDate(date) {
            let events = _.filter(this.events, (event) => {
                event.date = moment(event.event_date);
                switch (event.repeat_interval) {
                    case RepeatInterval.NO_REPEAT:
                        return (
                            event.date.format("YYYYMMDD") ===
                            date.format("YYYYMMDD")
                        );
                    case RepeatInterval.DAILY:
                        return true;
                    case RepeatInterval.EVERY_WEEKDAY:
                        return _.includes([1, 2, 3, 4, 5], date.day());
                    case RepeatInterval.WEEKLY:
                        return event[
                            "weekly_" + this.weekDayStrings[date.day()]
                        ];
                    case RepeatInterval.BIWEEKLY:
                        return event[
                            "weekly_" + this.weekDayStrings[date.day()]
                        ]
                            ? this.checkDateBiWeekly(date, event)
                            : false;
                    case RepeatInterval.MONTHLY:
                        if (event.monthly_day === null) {
                            return false;
                        }
                        if (event.monthly_week === "last") {
                            let last_day = moment([
                                this.calendar.year,
                                this.calendar.month - 1
                            ]).endOf("month");
                            while (
                                last_day.day() !== parseInt(event.monthly_day)
                            ) {
                                last_day.subtract(1, "day");
                            }
                            return last_day.isSame(date, "day");
                        }
                        return moment
                            .recur()
                            .every(event.monthly_day)
                            .daysOfWeek()
                            .every(
                                MonthlyWeek[event.monthly_week.toUpperCase()] -
                                    1
                            )
                            .weeksOfMonthByDay()
                            .matches(date);
                    case RepeatInterval.YEARLY:
                        return (
                            date.month() === event.date.month() &&
                            date.date() === event.date.date()
                        );
                    default:
                        return false;
                }
            });
            events = this.filterDeletedEvents(events, date);
            return _.sortBy(events, (event) =>
                event.time ? moment(event.time).format("HH:mm:ss") : "00:00:00"
            );
        },
        getEventStyle(event) {
            return {
                "font-family": CalendarUtils.getValidFontName(
                    this.calendar.font
                ),
                "font-size": this.calendar.font_size + "pt",
                color: event.color,
                "font-weight":
                    event.is_bold || this.calendar.font.includes("bold")
                        ? "bold"
                        : "normal"
            };
        },
        formatEventTime(event) {
            let date = moment(event.event_date);
            let time = null;
            if (!event.is_all_day) {
                time = date.format("YYYY-MM-DD") + " " + event.event_time;
                return moment(time).format("h:mm");
            }
        },
        /**
         * Get the event display text in html format.
         *
         * @param {Object} event
         * @param {Boolean} first_event True if the event is the first line of the date box.
         * @param {Boolean} first_line_overlaps_date
         * @returns {string}
         */
        getEventDisplayText(
            event,
            first_event = false,
            first_line_overlaps_date = false
        ) {
            let html = "";
            CalendarUtils.getEventDescriptionLines(
                event,
                this.calendar.font,
                this.calendar.font_size,
                first_event && first_line_overlaps_date
            ).forEach((line) => {
                if (html !== "") {
                    html += "<br>";
                }
                html += Utils.htmlentities(line);
            });
            return html;
        },
        /**
         * @param date
         * @returns {boolean}
         */
        checkDateBiWeekly(date, event) {
            let new_event = _.cloneDeep(event);
            let new_event_date = new_event.date;
            if (new_event.parent) {
                new_event_date = moment(new_event.parent.event_date);
            }
            let days = date.diff(new_event_date, "days");
            let weeks = 0;

            let currentDateEvent =
                event["weekly_" + this.weekDayStrings[new_event_date.day()]];

            if (days >= 0) {
                weeks = Math.ceil(days / 7);
                if (currentDateEvent && date.day() == new_event_date.day()) {
                    if (weeks % 2 == 0) {
                        return true;
                    } else {
                        return false;
                    }
                } else {
                    if (weeks % 2 != 0) {
                        return true;
                    } else {
                        return false;
                    }
                }
            } else {
                return false;
            }
        }
    }
};
</script>

<style lang="scss">
.digital-display-page {
    .top-banner {
        display: none;
    }
    .container {
        .main {
            padding: 0px !important;
        }
    }
}
.dd-calendar {
    height: 100%;
    font-family: Helvetica, serif;
    background-repeat: no-repeat;
    background-position: center center;
    background-size: cover;
    padding: 10px 25px !important;
    overflow: auto;

    table {
        border-collapse: collapse;
        width: 100%;

        thead {
            td {
                width: 219px;
                max-width: 219px;
                font-weight: bold;
                text-transform: uppercase;
                text-align: center;
                font-size: 24px;
                padding: 10px 0px;
            }
        }

        tbody {
            line-height: 9pt;

            td {
                width: 219px;
                min-height: 154px;
                max-width: 219px;
                overflow: hidden;
                background-color: #fff;

                &.hide-date {
                    background-color: transparent;
                }

                .date {
                    display: none;
                }

                .cell {
                    overflow: hidden;
                    max-width: 219px;
                    padding: 2px;

                    .description {
                        word-wrap: break-word;
                        margin-top: 4px;

                        .time {
                            width: 35px;
                            max-width: 35px;
                        }

                        .description-lines {
                            width: 130px;
                            @media only screen and (min-width: 1920px) {
                                width: 174px !important;
                            }
                        }

                        &:first-of-type {
                            /*margin-top: 10px;*/
                        }

                        &.all-day {
                            .time {
                                display: none;
                            }

                            .description-lines {
                                float: left;
                                margin-left: 17px;
                                text-align: center;
                            }
                        }
                    }
                }
            }

            &.no-theme td {
                .cell {
                    position: relative;

                    &.maxed-out {
                        background-color: pink;
                    }
                }

                border: 1px solid #c0c4cc;
                position: relative;

                &.hide-date {
                    border: none;

                    .cell.maxed-out {
                        background-color: transparent;
                    }
                }

                .date {
                    display: block;
                    position: absolute;
                    top: 5px;
                    right: 5px;
                    text-align: right;
                    width: 100%;
                    font-weight: bold;
                    font-size: 19px;
                    z-index: 1;
                }

                &.hide-date .date {
                    display: none;
                }
            }
        }
    }
}
</style>
